import React, { useState, useEffect } from 'react';
import { z } from 'zod';
import { safeUrl } from '../url';
import { LeadFormFields } from './leadForm';
// Named import (not default): under Node ESM/tsx the default import of the
// CJS styled-components build resolves to a namespace object, so styled.section
// is undefined. The named `styled` export resolves consistently in both the
// tsx server and the rspack client.
import { styled } from 'styled-components';
import type { BlockDefinition } from '../types';

// Home hero — clean rewrite of legacy molecules/HomeNewHero (Strapi
// sections.new-home-hero). No styled-system / next/image / framer-motion /
// isMobile(); responsive via CSS media queries so it is SSR-safe. The Zod
// schema IS the block's authored-props contract.
export const homeHeroSchema = z.object({
  headingLines: z.array(z.string().min(1)).min(1).max(4),
  title: z.string().optional(),
  description: z.string().optional(),
  bgImageUrl: z.string().optional(),
  primaryCta: z.object({ label: z.string(), href: safeUrl }).optional(),
  secondaryCta: z.object({ label: z.string(), href: safeUrl }).optional(),
});

type HomeHeroProps = z.infer<typeof homeHeroSchema>;

const Band = styled.section`
  position: relative;
  overflow: hidden;
  border-radius: 0 0 8rem 0;
  background: linear-gradient(93deg, #e8efff 0%, #ffffff 100%);
  padding: 12rem 1.6rem 4rem;

  @media (min-width: 64em) {
    display: flex;
    align-items: center;
    min-height: 70vh;
    padding: 0 2rem;
  }
`;

const Inner = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 112rem;
  margin: 0 auto;
`;

const BgImage = styled.img`
  display: none;

  @media (min-width: 64em) {
    display: block;
    position: absolute;
    top: 50%;
    right: clamp(2rem, 6vw, 12rem);
    transform: translateY(-50%);
    z-index: 0;
    width: 46%;
    max-width: 68rem;
    max-height: 82%;
    object-fit: contain;
    pointer-events: none;
  }
`;

const Heading = styled.h1`
  margin: 0;
  color: #0b1020;
  font-family: 'DM Sans', sans-serif;
  font-weight: 600;
  font-size: clamp(4.4rem, 6vw, 8rem);
  line-height: 1.15;
`;

const CtaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1.2rem;
  margin-top: 2.4rem;
`;

const Cta = styled.a<{ $variant: 'primary' | 'secondary' }>`
  display: inline-block;
  padding: 1.2rem 2.4rem;
  border-radius: 1.6rem;
  font-family: 'DM Sans', sans-serif;
  font-size: 1.6rem;
  font-weight: 500;
  text-decoration: none;
  cursor: pointer;
  ${({ $variant }) =>
    $variant === 'primary'
      ? 'background: #0a3aa8; color: #ffffff; border: 1px solid #0a3aa8;'
      : 'background: transparent; color: #0a3aa8; border: 1px solid #0a3aa8;'}
`;

const Lower = styled.div`
  display: grid;
  gap: 2.4rem;
  max-width: 144rem;
  margin: 3rem auto;
  padding: 3rem 1.6rem;

  @media (min-width: 64em) {
    grid-template-columns: 1.4fr 1fr;
    align-items: start;
    padding: 4.8rem 14.8rem;
  }
`;

const Title = styled.h2`
  margin: 0;
  color: #0b1020;
  font-family: 'DM Sans', sans-serif;
  font-weight: 600;
  font-size: clamp(2rem, 3vw, 3.6rem);
`;

const Description = styled.p`
  margin: 0;
  color: #3a4256;
  font-size: 1.6rem;
  line-height: 1.7;
`;

// ---- "Talk to Us" → Get in Touch modal (live opens a popup, not a page) ----
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: grid;
  place-items: center;
  padding: 2rem;
  background: rgba(6, 16, 32, 0.55);
`;

const ModalCard = styled.div`
  position: relative;
  width: 100%;
  max-width: 72rem;
  max-height: 92vh;
  overflow-y: auto;
  padding: clamp(2.4rem, 4vw, 4rem);
  border-radius: 2.4rem;
  background: #ffffff;
`;

const CloseBtn = styled.button`
  position: absolute;
  top: 1.6rem;
  right: 1.6rem;
  appearance: none;
  border: none;
  background: none;
  font-size: 2.4rem;
  line-height: 1;
  color: #4e4f52;
  cursor: pointer;
`;

const ModalTitle = styled.h2`
  margin: 0 0 2.4rem;
  color: #0b1020;
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 700;
  font-size: clamp(2.2rem, 3vw, 2.8rem);
`;

function GetInTouchModal({ onClose }: { onClose: () => void }) {
  // Close on a successful lead submit — the shared form's delegated handler fires
  // `atlas:lead-submitted` (then opens Calendly).
  useEffect(() => {
    const onSubmitted = () => onClose();
    window.addEventListener('atlas:lead-submitted', onSubmitted);
    return () => window.removeEventListener('atlas:lead-submitted', onSubmitted);
  }, [onClose]);

  return (
    <Overlay onClick={onClose} role="dialog" aria-modal="true" aria-label="Get in Touch">
      <ModalCard onClick={(e) => e.stopPropagation()}>
        <CloseBtn type="button" aria-label="Close" onClick={onClose}>&times;</CloseBtn>
        <ModalTitle>Get in Touch</ModalTitle>
        {/* Shared cross-tenant lead form (packages/blocks/leadForm.tsx). */}
        <LeadFormFields submitLabel="Submit Now" messageLabel="Message" />
      </ModalCard>
    </Overlay>
  );
}

function HomeHero({
  headingLines,
  title,
  description,
  bgImageUrl,
  primaryCta,
  secondaryCta,
}: HomeHeroProps) {
  const [contactOpen, setContactOpen] = useState(false);
  // Legacy hero always shows two buttons, defaulting to "Try Now" (external
  // signup) / "Talk to Us" (contact) when the CMS leaves them blank. Mirror that
  // so the hero never renders button-less, whatever the authored props.
  const primary = primaryCta ?? { label: 'Try Now', href: 'https://register.cymmetri.io/signup' };
  const secondary = secondaryCta ?? { label: 'Talk to Us', href: '/contact-us' };
  // Internal "contact" secondary CTA opens the Get-in-Touch modal (live behaviour)
  // instead of navigating to a page.
  const secondaryIsContact = !/^https?:/i.test(secondary.href) && /contact/i.test(secondary.href);
  const last = headingLines.length - 1;
  return (
    <>
      <Band>
        {bgImageUrl ? <BgImage src={bgImageUrl} alt="" /> : null}
        <Inner>
          <Heading>
            {/* Break before the LAST line only (legacy: "one. two.<br>three."),
                so multi-word headings wrap the way the live site does rather
                than stacking one word per line. */}
            {headingLines.map((line, i) => (
              <React.Fragment key={i}>
                {line}.{i < last ? (i === last - 1 ? <br /> : ' ') : null}
              </React.Fragment>
            ))}
          </Heading>
          <CtaRow>
            <Cta $variant="primary" href={primary.href} target="_blank" rel="noreferrer">
              {primary.label}
            </Cta>
            {secondaryIsContact ? (
              <Cta as="button" type="button" $variant="secondary" onClick={() => setContactOpen(true)}>
                {secondary.label}
              </Cta>
            ) : (
              <Cta $variant="secondary" href={secondary.href}>
                {secondary.label}
              </Cta>
            )}
          </CtaRow>
        </Inner>
      </Band>
      {title || description ? (
        <Lower>
          {title ? <Title>{title}</Title> : null}
          {description ? <Description>{description}</Description> : null}
        </Lower>
      ) : null}
      {contactOpen ? <GetInTouchModal onClose={() => setContactOpen(false)} /> : null}
    </>
  );
}

export const homeHeroBlock: BlockDefinition<HomeHeroProps> = {
  type: 'homeHero',
  schema: homeHeroSchema,
  Renderer: HomeHero,
};
